import React, { useState } from 'react';

import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { EllipsisText } from '@vk-hr-tek/ui/EllipsisText';
import { FilePreviewWithSnackbar } from '@vk-hr-tek/ui/FilePreviewWithSnackbar';
import { Button } from '@vk-hr-tek/ui/Button';
import { LinearProgressBar } from '@vk-hr-tek/ui/LinearProgressBar';
import { useInject } from '@vk-hr-tek/core/ioc';
import { FilesService } from '@vk-hr-tek/core/files';

import {
  EditorOtherValueStatistics,
  UserChoiceAnswerOptionStatistic,
} from '@app/gen/quiz';

import { SurveyAnalyticsOtherTextAnswer } from './SurveyAnalyticsOtherTextAnswer';

interface SurveyAnalyticsTextAnswerProps {
  type?: UserChoiceAnswerOptionStatistic['type'];
  text_value?: string;
  image_value_url?: string;
  image_caption?: string;
  is_edited: boolean;
  answer_count: number;
  percentage: number;
  otherValueStats?: EditorOtherValueStatistics;
  selected?: boolean;
}

export const SurveyAnalyticsTextAnswer = ({
  type,
  text_value: textValue,
  image_value_url: imageValueUrl,
  image_caption: imageCaption,
  is_edited: isEdited,
  answer_count: answerCount,
  percentage,
  otherValueStats,
  selected = false,
}: SurveyAnalyticsTextAnswerProps) => {
  const service = useInject(FilesService);
  const [isImageOpen, setIsImageOpen] = useState(false);

  const hasImagePreview =
    (type === 'image' || type === 'text_image') && !!imageValueUrl;
  const hasOtherAnswer = !!otherValueStats;

  const handleImageOpen = () => {
    setIsImageOpen(true);
  };

  const handleImageClose = () => {
    setIsImageOpen(false);
  };

  const loadDocument = async (url: string) => {
    const { file } = await service.getFile(url);

    return file;
  };

  if (hasOtherAnswer) {
    <SurveyAnalyticsOtherTextAnswer
      selected={selected}
      isEdited={isEdited}
      otherValueStats={otherValueStats}
    />;
  }

  return (
    <>
      <Box py="8" display="flex" flexDirection="column" gap="8">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="32"
        >
          <Box display="flex" alignItems="center" gap="8">
            {selected && (
              <Tooltip title="Выбранный ответ" placement="top">
                <Box width="16px" height="16px">
                  <Icon
                    name="check_circle_large"
                    size="16"
                    color="original.brand.primary"
                  />
                </Box>
              </Tooltip>
            )}

            {hasImagePreview && hasImagePreview && (
              <Box
                display="flex"
                alignItems="center"
                gap="4"
                cursor="pointer"
                onClick={handleImageOpen}
              >
                <Icon
                  name="picture_outline"
                  size="16"
                  color="original.brand.primary"
                />

                <Typography variant="body3" color="original.brand.primary">
                  Посмотреть
                </Typography>
              </Box>
            )}

            <EllipsisText rows={1} wordBreak="break-all">
              <Tooltip title={textValue} placement="top">
                <Typography variant="body3">{textValue}</Typography>
              </Tooltip>
            </EllipsisText>

            {isEdited && (
              <Tooltip
                title={
                  <Box display="flex" flexDirection="column" gap="8">
                    <Typography variant="subtitle2" color="bg.light.primary">
                      Ответ изменен
                    </Typography>

                    <Typography variant="caption" color="bg.light.primary">
                      В результатах отображаются голоса участников, оставленные
                      до изменений
                    </Typography>
                  </Box>
                }
                placement="top"
              >
                <Box width="16px" height="16px">
                  <Icon name="pen" size="16" color="stroke.secondary" />
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap="4">
            <Typography variant="body3" color="text.light.tertirary">
              {answerCount}
            </Typography>
            <Typography variant="body3">{`(${percentage}%)`}</Typography>
          </Box>
        </Box>

        <Box bgcolor="stroke.primary" radius="l">
          <LinearProgressBar
            variant="determinate"
            value={percentage}
            height={8}
            color="original.brand.primary"
            transparentBackground
          />
        </Box>
      </Box>

      {hasImagePreview && isImageOpen && (
        <FilePreviewWithSnackbar
          open={isImageOpen}
          onClose={handleImageClose}
          loadDocument={() => loadDocument(imageValueUrl)}
          withZoom
          snackbarMessage={
            <Box>
              <Typography variant="subtitle1" color="text.dark.primary">
                {imageCaption}
              </Typography>
            </Box>
          }
          action={
            <Box display="flex" justifyContent="flex-end">
              <Button
                onClick={handleImageClose}
                size="medium"
                variant="tertiaryDark"
              >
                Закрыть
              </Button>
            </Box>
          }
        />
      )}
    </>
  );
};
